/**
 * Our own field DSL.
 *
 * Blocks declare fields with these types rather than Puck's, so `packages/blocks` stays free
 * of `@puckeditor/core` and remains testable in plain Node. `buildPuckConfig.ts` in the web
 * app translates this union into Puck fields and injects the React components.
 *
 * The cost is one translation function. The benefit is that a breaking Puck release touches
 * one file instead of every block.
 */

/**
 * Where a piece of text sits, which decides which voice rules apply to it.
 *
 * The distinction is not cosmetic: "Collectiv" and "Collective" are misspellings in body copy
 * but deliberate SEO targets in meta and keyword fields, so the brand-spelling rule must fire
 * on `body` only.
 */
export type LintScope = 'body' | 'heading' | 'meta' | 'keyword' | 'none';

export interface SelectOption {
  label: string;
  value: string;
}

export type FieldSpec =
  | { type: 'text'; label: string; help?: string; maxChars?: number; lint?: LintScope }
  | { type: 'textarea'; label: string; help?: string; maxChars?: number; lint?: LintScope }
  | { type: 'richText'; label: string; help?: string; profile: 'article' | 'compact' }
  | { type: 'select'; label: string; help?: string; options: SelectOption[] }
  | { type: 'radio'; label: string; help?: string; options: SelectOption[] }
  | { type: 'number'; label: string; help?: string; min?: number; max?: number }
  | { type: 'boolean'; label: string; help?: string }
  | { type: 'date'; label: string; help?: string }
  | { type: 'image'; label: string; help?: string; role?: 'hero' | 'inline' | 'og' }
  | { type: 'icon'; label: string; help?: string }
  | { type: 'link'; label: string; help?: string; internalOnly?: boolean }
  | { type: 'cta'; label: string; help?: string }
  | { type: 'padding'; label: string; help?: string }
  | { type: 'author'; label: string; help?: string }
  | { type: 'slot'; label: string; help?: string; allow: string[]; min?: number; max?: number }
  | { type: 'object'; label: string; help?: string; fields: Record<string, FieldSpec> }
  | {
      type: 'array';
      label: string;
      help?: string;
      of: Record<string, FieldSpec>;
      min?: number;
      max?: number;
      /** Which sub-field to show on the collapsed row. */
      summaryField?: string;
    };

/**
 * The value a `cta` field produces.
 *
 * `analyticsId` populates both the button's DOM id and the `pos` argument of
 * `handleConversionClick(loc, pos, utmContent)` — on the live site those are the same string
 * (`navBarCTA`, `homeHeroCTA`), so the field produces one value for both.
 */
export interface CtaValue {
  label: string;
  href: string;
  analyticsId: string;
  location: string;
  utmContent: string;
}

/**
 * Padding, as one control that can be split.
 *
 * Linked is the normal case — a card wants the same space all round — so the two axes move
 * together until someone unlinks them. `y` stays authoritative while linked, and `x` follows,
 * which means unlinking never loses the value you already set.
 */
export interface PaddingValue {
  linked: boolean;
  /** Top and bottom, as a spacing-scale step. */
  y: string;
  /** Left and right. */
  x: string;
}

export const paddingValue = (y: string, x = y, linked = y === x): PaddingValue => ({
  linked,
  y,
  x,
});

/** Tolerates the older shape, where the two axes were separate props. */
export function readPadding(
  value: unknown,
  fallback: PaddingValue = { linked: true, y: '0', x: '0' },
): PaddingValue {
  if (value && typeof value === 'object' && 'y' in value) {
    const v = value as Partial<PaddingValue>;
    return { linked: v.linked ?? true, y: v.y ?? fallback.y, x: v.x ?? fallback.x };
  }
  if (typeof value === 'string') return { linked: true, y: value, x: value };
  return fallback;
}

export interface LinkValue {
  href: string;
  label: string;
  newTab?: boolean;
}

/**
 * Coerce a `richText` field value to an HTML string.
 *
 * Puck *stores* rich text as an HTML string, but by default it replaces the prop with a React
 * element before calling `render()` — its `richtext` field transform wraps the value in an
 * inline Tiptap editor. That element reaching `dangerouslySetInnerHTML` is what rendered a
 * literal `[object Object]` on the canvas while the export, which reads the stored data rather
 * than the transformed prop, emitted the real markup. Preview and export drifting apart is the
 * one thing this architecture exists to prevent.
 *
 * `EditorHost` now overrides that transform so the prop stays a string. This function is the
 * second line of defence: whatever arrives, a block gets a string and never stringifies an
 * object into the page.
 */
export function readRichText(value: unknown): string {
  if (typeof value === 'string') return value;
  // Tiptap JSON, a React element, or anything else non-textual: render nothing rather than
  // "[object Object]". An empty block is obviously empty; a mangled one looks like a bug in
  // the page rather than a bug in the tool.
  return '';
}
